/**
 * Assistance Type to Transaction Type ID Mapping
 * Maps frontend assistance type strings to database types table IDs
 */

import type { AssistanceType } from '../types/visit-wizard'
import { ASSISTANCE_TYPE_LABELS } from '../types/visit-wizard'

/**
 * Transaction type IDs from types table in database
 * These correspond to the different legal assistance categories
 */
export const ASSISTANCE_TRANSACTION_TYPE_IDS: Record<AssistanceType, number> = {
  'mediation-conciliation': 300, // MEDIATION AND CONCILIATION
  'legal-counselling': 301, // LEGAL COUNSELLING
  'documentation': 303, // DOCUMENTATION
  'oaths-administered': 304, // OATHS ADMINISTERED
  'jail-visit': 305, // JAIL VISIT
  'representation-court': 306, // REPRESENTATION IN COURT/QUASI JUDICIAL BODIES
  'representation-appellate': 307, // REPRESENTATION BEFORE THE APPELLATE COURT
  'prosecutor-office': 308, // PROSECUTOR'S OFFICE
  'labor-cases': 309, // LABOR CASES
  'administrative-case': 310, // ADMINISTRATIVE CASE PROPER
  'outreach': 312, // LEGAL INFORMATION AND DISSEMINATION OUTREACH
}

/**
 * Get transaction type ID for an assistance type
 */
export function getTransactionTypeId(assistanceType: AssistanceType): number {
  return ASSISTANCE_TRANSACTION_TYPE_IDS[assistanceType]
}

/**
 * Reverse lookup: transaction type ID (from types table) -> human-readable assistance label
 */
export const TRANSACTION_TYPE_ID_LABELS: Record<number, string> = Object.fromEntries(
  (Object.entries(ASSISTANCE_TRANSACTION_TYPE_IDS) as [AssistanceType, number][]).map(
    ([assistanceType, typeId]) => [typeId, ASSISTANCE_TYPE_LABELS[assistanceType]]
  )
)

/**
 * Get assistance label for a transaction type ID, falling back for unknown IDs
 */
export function getAssistanceLabelFromTypeId(transactionTypeId?: number | null): string {
  if (transactionTypeId == null) return 'Assistance'
  return TRANSACTION_TYPE_ID_LABELS[transactionTypeId] || `Assistance (Type ${transactionTypeId})`
}

/**
 * Reverse lookup: transaction type ID -> assistance type key
 * Used to pick which assistance-specific form to render when editing a saved transaction
 */
export const ASSISTANCE_TYPE_BY_TRANSACTION_TYPE_ID: Record<number, AssistanceType> = Object.fromEntries(
  (Object.entries(ASSISTANCE_TRANSACTION_TYPE_IDS) as [AssistanceType, number][]).map(
    ([assistanceType, typeId]) => [typeId, assistanceType]
  )
) as Record<number, AssistanceType>

/**
 * Get the assistance type key for a transaction type ID, if known
 */
export function getAssistanceTypeFromTransactionTypeId(
  transactionTypeId?: number | null
): AssistanceType | undefined {
  if (transactionTypeId == null) return undefined
  return ASSISTANCE_TYPE_BY_TRANSACTION_TYPE_ID[transactionTypeId]
}
